import React, { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Building2, Save, CheckCircle2, Loader2 } from "lucide-react";
import UserSettings from "../components/UserSettings";

const EMPTY = {
  name: "", address_line: "", postcode: "", city: "", country: "DE",
  vat_id: "", tax_number: "", register_id: "", iban: "", bic: "", email: "", reply_email: "", phone: "",
  default_payment_terms: "Zahlbar innerhalb von 14 Tagen ohne Abzug per Überweisung.",
};

export default function CompanyProfile() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/company");
        setForm({ ...EMPTY, ...data });
      } catch (e) { setErr(formatErr(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr(""); setSaved(false);
    try {
      await api.put("/profile/company", form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) { setErr(formatErr(e)); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="card p-10 flex items-center gap-3 text-slate-300" data-testid="profile-loading">
      <Loader2 size={18} className="animate-spin text-blue-400"/>Lade Stammdaten...
    </div>;
  }

  return (
    <div className="space-y-10 max-w-4xl mx-auto">
      <UserSettings/>

      <div className="h-px bg-slate-800"></div>

      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
          <Building2 size={22} className="text-blue-400"/>
        </div>
        <div>
          <h1 className="font-display text-3xl font-bold">Firmen-Stammdaten</h1>
          <p className="text-slate-400 text-sm mt-1">Diese Daten werden automatisch als Verkäufer in jede neue E-Rechnung übernommen.</p>
        </div>
      </div>

      {err && <div className="card border-rose-500/40 bg-rose-500/5 p-3 text-rose-200 text-sm" data-testid="profile-error">{err}</div>}

      <form onSubmit={submit} className="card p-6 space-y-6" data-testid="company-profile-form">
        <Section title="Firma">
          <Grid cols={2}>
            <F label="Firmenname" value={form.name} onChange={onChange("name")} tid="profile-name-input"/>
            <F label="E-Mail (Rechnungsersteller)" type="email" value={form.email} onChange={onChange("email")} tid="profile-email-input"/>
            <F label="Telefon" value={form.phone} onChange={onChange("phone")} tid="profile-phone-input"/>
            <div>
              <F label="Antwort-E-Mail (Kunden-Antworten)" type="email" value={form.reply_email} onChange={onChange("reply_email")} tid="profile-reply-email-input" placeholder="rechnung@ihre-firma.de"/>
              <p className="text-xs text-slate-500 mt-1">An diese Adresse gehen Antworten auf Ihre Rechnungs-E-Mails. Sie erscheint auch im Absenderblock der Mail.</p>
            </div>
          </Grid>
        </Section>

        <Section title="Anschrift">
          <Grid cols={2}>
            <F label="Straße + Nr." value={form.address_line} onChange={onChange("address_line")} tid="profile-address-input"/>
            <div className="grid grid-cols-3 gap-3">
              <F label="PLZ" value={form.postcode} onChange={onChange("postcode")} tid="profile-postcode-input"/>
              <div className="col-span-2"><F label="Stadt" value={form.city} onChange={onChange("city")} tid="profile-city-input"/></div>
            </div>
            <F label="Land (ISO)" value={form.country} onChange={onChange("country")} tid="profile-country-input"/>
          </Grid>
        </Section>

        <Section title="Steuer-Kennungen">
          <Grid cols={2}>
            <F label="USt-IdNr." value={form.vat_id} onChange={onChange("vat_id")} tid="profile-vatid-input" placeholder="DE123456789"/>
            <F label="Steuernummer" value={form.tax_number} onChange={onChange("tax_number")} tid="profile-taxnum-input" placeholder="121/5802/5048"/>
            <F label="Handelsregister-Nr. (optional)" value={form.register_id} onChange={onChange("register_id")} tid="profile-register-input" placeholder="HRB 12345, Amtsgericht Köln"/>
          </Grid>
        </Section>

        <Section title="Bankverbindung">
          <Grid cols={2}>
            <F label="IBAN" value={form.iban} onChange={onChange("iban")} tid="profile-iban-input" placeholder="DE19 3106 0517 ..."/>
            <F label="BIC" value={form.bic} onChange={onChange("bic")} tid="profile-bic-input" placeholder="GENODED1MRB"/>
          </Grid>
        </Section>

        <Section title="Standard-Zahlungsbedingung">
          <textarea rows={2} className="textarea" value={form.default_payment_terms} onChange={onChange("default_payment_terms")} data-testid="profile-payment-terms-input"/>
        </Section>

        <div className="flex items-center justify-end gap-3 pt-1 border-t border-slate-800">
          {saved && <span className="chip chip-valid" data-testid="profile-saved-chip"><CheckCircle2 size={12}/>Gespeichert</span>}
          <button className="btn-primary" type="submit" disabled={saving} data-testid="profile-save-button">
            {saving ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>}
            {saving ? "Speichere..." : "Stammdaten speichern"}
          </button>
        </div>
      </form>
    </div>
  );
}

const Section = ({title, children}) => (
  <div>
    <div className="font-display font-semibold text-slate-100 mb-3">{title}</div>
    {children}
  </div>
);
const Grid = ({cols=2, children}) => <div className={`grid grid-cols-1 sm:grid-cols-${cols} gap-3`}>{children}</div>;
const F = ({label, value, onChange, type="text", placeholder, tid}) => (
  <div><label className="label">{label}</label><input type={type} className="input" value={value||""} onChange={onChange} placeholder={placeholder} data-testid={tid}/></div>
);
